import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertCircle, Edit3 } from 'lucide-react';

const FALLBACK_COMPARISON_METHOD = {
  label: 'Ordered text match',
  description: 'Compares wording and sequence so step order changes lower the score.',
};

const getSimilarityScore = (similarity) => {
  if (typeof similarity === 'number') return similarity;
  if (!similarity || typeof similarity !== 'object') return 0;
  if (typeof similarity.score === 'number') return similarity.score;
  if (typeof similarity.average === 'number') return similarity.average;
  if (typeof similarity.sequence_matcher === 'number') return similarity.sequence_matcher;
  return 0;
};

const getSimilarityTextColor = (similarity) => {
  if (similarity >= 95) return 'text-green-700';
  if (similarity >= 85) return 'text-blue-700';
  if (similarity >= 70) return 'text-amber-700';
  return 'text-red-700';
};

const getSimilarityBadgeColor = (similarity) => {
  if (similarity >= 95) return 'border-green-200 bg-green-50 text-green-700';
  if (similarity >= 85) return 'border-blue-200 bg-blue-50 text-blue-700';
  if (similarity >= 70) return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-red-200 bg-red-50 text-red-700';
};

const getSimilarityPanelColor = (similarity) => {
  if (similarity >= 95) return 'border-green-200 bg-green-50/70';
  if (similarity >= 85) return 'border-blue-200 bg-blue-50/70';
  if (similarity >= 70) return 'border-amber-200 bg-amber-50/70';
  return 'border-red-200 bg-red-50/70';
};

const formatFieldName = (fieldName) => {
  const labels = {
    test_case_no: 'Test Case ID',
    test_case_id: 'Case Reference',
    test_description: 'Test Description',
    User_Role: 'User Role',
    tab_flows: 'Tab Flows',
    test_steps: 'Test Steps',
    expected_result: 'Expected Result',
    user_inputs: 'User Inputs',
    user_input_sets: 'User Input Sets',
    active_input_set_index: 'Active Input Set',
  };
  return labels[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatCaseTitle = (testCase, index) => {
  const explicitId = String(testCase?.test_case_no || testCase?.test_case_id || '').trim();
  if (explicitId) return explicitId;
  return `Test Case ${index + 1}`;
};

const formatLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const parseStructuredValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed);
      } catch (error) {
        return trimmed;
      }
    }
    return trimmed;
  }
  return value;
};

const isPrimitive = (value) =>
  value === null || value === undefined || ['string', 'number', 'boolean'].includes(typeof value);

const renderPrimitiveValue = (value) => {
  const text = String(value ?? '').trim();
  return text || '(empty)';
};

const toDiffLines = (value) => {
  const parsed = parseStructuredValue(value);

  if (isPrimitive(parsed)) {
    const text = renderPrimitiveValue(parsed);
    if (text === '(empty)') return [];
    return text.split('\n').map((line) => line.trimEnd());
  }

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return [];

    if (parsed.every((item) => isPrimitive(item))) {
      return parsed.map((item) => `- ${renderPrimitiveValue(item)}`);
    }

    if (parsed.every((item) => isFieldValueObject(item))) {
      return parsed.map((item) => `${formatLabel(item.field)}: ${renderPrimitiveValue(item.value)}`);
    }

    try {
      return JSON.stringify(parsed, null, 2).split('\n');
    } catch (error) {
      return [String(parsed)];
    }
  }

  const entries = Object.entries(parsed || {});
  if (entries.length === 0) return [];

  const lines = [];
  entries.forEach(([key, entryValue]) => {
    const label = formatLabel(key);
    const parsedEntry = parseStructuredValue(entryValue);
    if (isPrimitive(parsedEntry)) {
      lines.push(`${label}: ${renderPrimitiveValue(parsedEntry)}`);
      return;
    }
    lines.push(`${label}:`);
    toDiffLines(parsedEntry).forEach((childLine) => {
      lines.push(`  ${childLine}`);
    });
  });
  return lines;
};

const buildLineDiff = (originalLines, modifiedLines) => {
  const n = originalLines.length;
  const m = modifiedLines.length;
  const lcs = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      if (originalLines[i] === modifiedLines[j]) {
        lcs[i][j] = lcs[i + 1][j + 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
  }

  const diff = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (originalLines[i] === modifiedLines[j]) {
      diff.push({ type: 'context', text: originalLines[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      diff.push({ type: 'removed', text: originalLines[i] });
      i += 1;
    } else {
      diff.push({ type: 'added', text: modifiedLines[j] });
      j += 1;
    }
  }

  while (i < n) {
    diff.push({ type: 'removed', text: originalLines[i] });
    i += 1;
  }

  while (j < m) {
    diff.push({ type: 'added', text: modifiedLines[j] });
    j += 1;
  }

  return diff;
};

const DiffLine = ({ entry }) => {
  if (entry.type === 'added') {
    return (
      <div className="bg-emerald-100/80 text-emerald-900">
        <span className="inline-block w-6 select-none text-center font-bold">+</span>
        <span className="whitespace-pre-wrap break-words">{entry.text || ' '}</span>
      </div>
    );
  }

  if (entry.type === 'removed') {
    return (
      <div className="bg-rose-100/80 text-rose-900">
        <span className="inline-block w-6 select-none text-center font-bold">-</span>
        <span className="whitespace-pre-wrap break-words">{entry.text || ' '}</span>
      </div>
    );
  }

  return (
    <div className="text-slate-700">
      <span className="inline-block w-6 select-none text-center"> </span>
      <span className="whitespace-pre-wrap break-words">{entry.text || ' '}</span>
    </div>
  );
};

const ExactDiffView = ({ original, modified }) => {
  const originalLines = toDiffLines(original);
  const modifiedLines = toDiffLines(modified);
  const diffLines = buildLineDiff(originalLines, modifiedLines);
  const hasVisibleChanges = diffLines.some((entry) => entry.type === 'added' || entry.type === 'removed');

  if (!hasVisibleChanges) {
    return null;
  }

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-slate-50/80">
      <div className="border-b border-slate-200 bg-white/70 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
        Exact Changes
      </div>
      <div className="max-h-72 overflow-auto bg-gradient-to-b from-slate-50 to-white px-2 py-2 font-mono text-xs leading-5">
        {diffLines.map((entry, index) => (
          <DiffLine key={`${entry.type}-${index}`} entry={entry} />
        ))}
      </div>
    </div>
  );
};

const isFieldValueObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return 'field' in value && 'value' in value;
};

const FieldComparison = ({ fieldName, field, comparisonMethod }) => {
  if (!field?.changed) return null;

  const similarityScore = getSimilarityScore(field.similarity);

  return (
    <div className={`rounded-xl border p-4 ${getSimilarityPanelColor(similarityScore)}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h5 className="font-semibold text-slate-900">{formatFieldName(fieldName)}</h5>
          <p className={`mt-1 text-sm font-medium ${getSimilarityTextColor(similarityScore)}`}>
            Match score: {similarityScore.toFixed(2)}%
          </p>
        </div>
        <div className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
          <Edit3 className="h-3.5 w-3.5" />
          Changed
        </div>
      </div>

      <ExactDiffView original={field.original} modified={field.modified} />

      <div className="mt-4 flex flex-wrap gap-4 border-t border-slate-200 pt-3 text-xs text-slate-600">
        <span>
          Length: {field.length_original} to {field.length_modified}
          <span className={`ml-1 font-semibold ${field.length_change >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
            ({field.length_change > 0 ? '+' : ''}{field.length_change})
          </span>
        </span>
        <span>Method: {comparisonMethod.label}</span>
      </div>
    </div>
  );
};

export const TestCaseComparisonViewer = ({
  comparisons = [],
  comparisonMethod = FALLBACK_COMPARISON_METHOD,
  changedCount = null,
}) => {
  const [expandedTestCase, setExpandedTestCase] = useState(null);
  const resolvedComparisonMethod = comparisonMethod?.label
    ? comparisonMethod
    : FALLBACK_COMPARISON_METHOD;

  const changedComparisons = comparisons
    .map((testCase, index) => {
      const changedFieldEntries = Object.entries(testCase?.fields || {}).filter(([, field]) => field?.changed);
      return {
        ...testCase,
        _viewerIndex: index,
        changedFieldEntries,
      };
    })
    .filter((testCase) => testCase.has_changes || testCase.changedFieldEntries.length > 0);

  const totalChangedCases = typeof changedCount === 'number' ? changedCount : changedComparisons.length;
  const totalChangedFields = changedComparisons.reduce(
    (sum, testCase) => sum + testCase.changedFieldEntries.length,
    0
  );

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-5 py-4">
        <h3 className="text-base font-semibold text-slate-900">Changed case details</h3>
        <p className="mt-1 text-xs text-slate-600">
          {totalChangedCases} case{totalChangedCases === 1 ? '' : 's'} changed across {totalChangedFields} edited field{totalChangedFields === 1 ? '' : 's'}.
          {' '}Method: {resolvedComparisonMethod.label}.
        </p>
      </div>

      {changedComparisons.length === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 p-8 text-center shadow-sm">
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-emerald-500/60 bg-white/90">
            <AlertCircle className="h-6 w-6 text-emerald-600" />
          </div>
          <p className="text-lg font-semibold text-emerald-900">No human edits detected</p>
          <p className="mt-1 text-sm text-emerald-800">
            Current version still matches the initial AI baseline across all tracked fields.
          </p>
          <p className="mt-2 text-xs font-medium text-emerald-700/90">
            {comparisons.length} case{comparisons.length === 1 ? '' : 's'} compared in this version.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {changedComparisons.map((testCase, index) => {
            const caseKey = String(
              testCase?.test_case_id
              || testCase?.test_case_no
              || `${testCase?._viewerIndex ?? index}`
            );
            const isExpanded = expandedTestCase === caseKey;
            const overallSimilarity = Number(testCase?.overall_similarity || 0);
            const description = String(testCase?.test_description || '').trim();

            return (
              <div key={caseKey} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <button
                  type="button"
                  onClick={() => setExpandedTestCase(isExpanded ? null : caseKey)}
                  className="flex w-full items-start justify-between gap-4 p-4 text-left transition-colors hover:bg-slate-50"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className={`rounded-full border px-3 py-1 text-sm font-semibold ${getSimilarityBadgeColor(overallSimilarity)}`}>
                      {overallSimilarity.toFixed(2)}%
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-slate-900">
                        {formatCaseTitle(testCase, index)}
                      </h4>
                      <p className="mt-1 text-sm text-slate-600">
                        {testCase.changedFieldEntries.length} changed field{testCase.changedFieldEntries.length === 1 ? '' : 's'}
                        {typeof testCase.tracked_fields_count === 'number'
                          ? ` out of ${testCase.tracked_fields_count} tracked`
                          : ''}
                        {testCase.category ? ` · ${testCase.category}` : ''}
                      </p>
                      {description && (
                        <p className="mt-2 line-clamp-2 text-sm text-slate-500">{description}</p>
                      )}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="mt-1 h-5 w-5 flex-shrink-0 text-slate-400" />
                  ) : (
                    <ChevronDown className="mt-1 h-5 w-5 flex-shrink-0 text-slate-400" />
                  )}
                </button>

                {isExpanded && (
                  <div className="space-y-3 border-t border-slate-200 bg-slate-50 p-4">
                    {testCase.changedFieldEntries.map(([fieldName, field]) => (
                      <FieldComparison
                        key={fieldName}
                        fieldName={fieldName}
                        field={field}
                        comparisonMethod={resolvedComparisonMethod}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TestCaseComparisonViewer;
